<script setup lang="ts">
import { ref } from 'vue'
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { IconChevronLeft } from '@tabler/icons-vue'

interface Props {
  modelValue: boolean
  title?: string
  width?: 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'full'
  noPadding?: boolean
  hideClose?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  title: undefined,
  width: 'lg',
  noPadding: false,
  hideClose: false,
})

const panelRef = ref<HTMLElement | null>(null)

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
}>()

const close = () => {
  emit('update:modelValue', false)
}

const widthClass: Record<string, string> = {
  md: 'w-screen sm:w-[400px]',
  lg: 'w-screen sm:w-[480px]',
  xl: 'w-screen sm:w-[520px]',
  '2xl': 'w-screen sm:w-[600px]',
  '3xl': 'w-screen md:w-[800px]',
  full: 'w-screen sm:w-[calc(100vw-80px)]',
}
</script>

<template>
  <TransitionRoot appear :show="modelValue" as="template">
    <Dialog as="div" class="relative z-50" :initial-focus="panelRef" @close="close">
      <!-- Backdrop -->
      <TransitionChild
        enter="transition ease-out duration-200"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="transition ease-in duration-150"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/40" />
      </TransitionChild>

      <!-- Panel -->
      <div class="fixed inset-0 overflow-hidden">
        <div class="absolute inset-0 overflow-hidden">
          <div class="pointer-events-none fixed inset-y-0 right-0 flex max-w-full">
            <TransitionChild
              enter="transition ease-out duration-300"
              enter-from="translate-x-full"
              enter-to="translate-x-0"
              leave="transition ease-in duration-200"
              leave-from="translate-x-0"
              leave-to="translate-x-full"
            >
              <DialogPanel
                ref="panelRef"
                class="pointer-events-auto h-full max-w-[100vw] bg-base-400 border-l border-white/[0.06] flex flex-col shadow-[-8px_0_40px_rgba(0,0,0,0.5)] focus:outline-none"
                :class="widthClass[props.width]"
                tabindex="-1"
              >
                <!-- Header -->
                <div v-if="title || $slots.header" class="px-6 py-5 border-b border-white/[0.06] flex items-center gap-3">
                  <button v-if="!hideClose" class="rail-btn !w-8 !h-8" @click="close">
                    <IconChevronLeft class="w-4 h-4" />
                  </button>
                  <slot name="header">
                    <h2 class="font-display text-xl font-bold text-gray-100">{{ title }}</h2>
                  </slot>
                </div>

                <!-- Floating back button when no header -->
                <button
                  v-if="!(title || $slots.header) && !hideClose"
                  class="absolute top-3 left-3 z-10 rail-btn !w-8 !h-8"
                  @click="close"
                >
                  <IconChevronLeft class="w-4 h-4" />
                </button>

                <!-- Content (scrollable) -->
                <div class="flex-1 overflow-y-auto" :class="noPadding ? '' : 'p-6'">
                  <slot />
                </div>

                <!-- Footer (optional) -->
                <div v-if="$slots.footer" class="px-6 py-4 border-t border-white/[0.06]">
                  <slot name="footer" />
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
